import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { CardActionArea } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import FileTabCard from "components/FileTabCard";
import FileTabDetails from "components/FileTabDetails";
import { RenderFile } from "components/RenderFile";
import { downloadFile, formatBytes } from "helpers";
import { FileInputType, FileType } from "operations/schema/schema";
import { FC, useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useAppDispatch } from "store";
import { openFilePreview } from "store/slices/dialogs/filePreview.store";

export interface FilePreviewProps {
  file: FileInputType | FileType;
  onDelete?: () => void;
  onEdit?: () => void;
  downloadOnCardClick?: boolean;
  readonly?: boolean;
  disableEdit?: boolean;
}

const PreviewExtensions = [
  "png",
  "jpg",
  "jpeg",
  "bmp",
  "gif",
  "tif",
  "tiff",
  "pdf",
  "mp4",
  "ogg",
  "webm",
];

export const FilePreview: FC<FilePreviewProps> = (props) => {
  const { file, onDelete, onEdit, downloadOnCardClick, readonly, disableEdit } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const handleDelete = useCallback(
    (event: any) => {
      if (!onDelete) return;
      event.preventDefault();
      onDelete();
    },
    [onDelete]
  );

  const isPreviewable = useMemo(() => {
    // eslint-disable-next-line
    const parts = file.name?.split(".");
    console.log(parts);
    if (!parts || parts.length !== 2) return false;
    const ext = parts[1];
    return PreviewExtensions.includes(ext);
  }, [file.name]);

  return (
    <Grid item xs={12} width="100%">
      <FileTabCard key={file.name} data-testid={`FilePreview-FileTabCard-${file.name}`}>
        <CardActionArea
          onDoubleClick={() => {
            if (downloadOnCardClick) {
              downloadFile(file);
            }
          }}
          onClick={() => {
            if (isPreviewable) {
              dispatch(openFilePreview({ file }));
            } else if (downloadOnCardClick) {
              downloadFile(file);
            }
          }}
          sx={{
            cursor: isPreviewable || downloadOnCardClick ? "pointer" : "default",
            display: "flex",
            ".MuiCardActionArea-focusHighlight": {
              background: "transparent",
            },
          }}
          disableRipple
          disableTouchRipple
        >
          <RenderFile file={file} />
          <FileTabDetails>
            <CardContent>
              <Typography
                component="p"
                variant="body2"
                sx={{
                  wordWrap: "break-word",
                }}
              >
                {file.name}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {file?.data?.length && formatBytes(file.data.length)}
              </Typography>
            </CardContent>
          </FileTabDetails>
        </CardActionArea>
        {!readonly && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "column",
            }}
          >
            {onDelete && (
              <IconButton
                aria-label={intl.formatMessage({ id: "general.delete" })}
                onClick={handleDelete}
                data-testid="FilePreview-DeleteButton"
                size="large"
              >
                <DeleteIcon color="error" />
              </IconButton>
            )}
            {file.data?.startsWith("data:image/") && onEdit && !disableEdit && (
              <IconButton
                aria-label={intl.formatMessage({ id: "general.edit" })}
                onClick={onEdit}
                data-testid="FilePreview-EditButton"
                size="large"
              >
                <EditIcon color="action" />
              </IconButton>
            )}
          </div>
        )}
      </FileTabCard>
    </Grid>
  );
};
